// Port of Ui::LayoutMediaGroup from Telegram Desktop (ui/grouped_layout.cpp, GPLv3).

export interface MosaicRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface MosaicLayout {
	width: number;
	height: number;
	items: MosaicRect[];
}

export interface MosaicOptions {
	width: number;
	spacing: number;
	minWidth?: number;
}

type Proportion = "w" | "n" | "q";

interface Params {
	ratios: number[];
	proportions: string;
	maxWidth: number;
	maxHeight: number;
	minWidth: number;
	spacing: number;
	averageRatio: number;
}

const rect = (x: number, y: number, width: number, height: number): MosaicRect => ({ x, y, width, height });

const toRatio = (ratio: number) => (Number.isFinite(ratio) && ratio > 0 ? ratio : 1);

const toProportion = (ratio: number): Proportion => (ratio > 1.2 ? "w" : ratio < 0.8 ? "n" : "q");

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

const layoutOne = ({ ratios, maxWidth }: Params) => [rect(0, 0, maxWidth, Math.round(maxWidth / ratios[0]))];

const layoutTwoTopBottom = ({ ratios, maxWidth, maxHeight, spacing }: Params) => {
	const height = Math.round(Math.min(maxWidth / ratios[0], maxWidth / ratios[1], (maxHeight - spacing) / 2));
	return [rect(0, 0, maxWidth, height), rect(0, height + spacing, maxWidth, height)];
};

const layoutTwoLeftRightEqual = ({ ratios, maxWidth, maxHeight, spacing }: Params) => {
	const width = Math.floor((maxWidth - spacing) / 2);
	const height = Math.round(Math.min(width / ratios[0], width / ratios[1], maxHeight));
	return [rect(0, 0, width, height), rect(width + spacing, 0, width, height)];
};

const layoutTwoLeftRight = ({ ratios, maxWidth, maxHeight, minWidth, spacing }: Params) => {
	const available = maxWidth - spacing;
	const minimalWidth = Math.round(minWidth * 1.5);
	const secondWidth = Math.min(
		Math.round(Math.max(0.4 * available, available / ratios[0] / (1 / ratios[0] + 1 / ratios[1]))),
		available - minimalWidth,
	);
	const firstWidth = available - secondWidth;
	const height = Math.min(maxHeight, Math.round(Math.min(firstWidth / ratios[0], secondWidth / ratios[1])));
	return [rect(0, 0, firstWidth, height), rect(firstWidth + spacing, 0, secondWidth, height)];
};

const layoutTwo = (params: Params) => {
	const { proportions, ratios, averageRatio, maxWidth, maxHeight } = params;
	if (proportions === "ww" && averageRatio > 1.4 * (maxWidth / maxHeight) && ratios[1] - ratios[0] < 0.2)
		return layoutTwoTopBottom(params);
	if (proportions === "ww" || proportions === "qq") return layoutTwoLeftRightEqual(params);
	return layoutTwoLeftRight(params);
};

const layoutThreeLeftAndOther = ({ ratios, maxWidth, maxHeight, minWidth, spacing }: Params) => {
	const firstHeight = maxHeight;
	const thirdHeight = Math.round(
		Math.min((maxHeight - spacing) / 2, (ratios[1] * (maxWidth - spacing)) / (ratios[2] + ratios[1])),
	);
	const secondHeight = firstHeight - thirdHeight - spacing;
	const rightWidth = Math.max(
		minWidth,
		Math.round(Math.min((maxWidth - spacing) / 2, thirdHeight * ratios[2], secondHeight * ratios[1])),
	);
	const leftWidth = Math.min(Math.round(firstHeight * ratios[0]), maxWidth - spacing - rightWidth);
	const rightX = leftWidth + spacing;
	return [
		rect(0, 0, leftWidth, firstHeight),
		rect(rightX, 0, rightWidth, secondHeight),
		rect(rightX, secondHeight + spacing, rightWidth, thirdHeight),
	];
};

const layoutThreeTopAndOther = ({ ratios, maxWidth, maxHeight, spacing }: Params) => {
	const firstHeight = Math.round(Math.min(maxWidth / ratios[0], (maxHeight - spacing) * 0.66));
	const secondWidth = Math.floor((maxWidth - spacing) / 2);
	const secondHeight = Math.min(
		maxHeight - firstHeight - spacing,
		Math.round(Math.min(secondWidth / ratios[1], secondWidth / ratios[2])),
	);
	const thirdWidth = maxWidth - secondWidth - spacing;
	const bottomY = firstHeight + spacing;
	return [
		rect(0, 0, maxWidth, firstHeight),
		rect(0, bottomY, secondWidth, secondHeight),
		rect(secondWidth + spacing, bottomY, thirdWidth, secondHeight),
	];
};

const layoutThree = (params: Params) =>
	params.proportions[0] === "n" ? layoutThreeLeftAndOther(params) : layoutThreeTopAndOther(params);

const layoutFourTopAndOther = ({ ratios, maxWidth, maxHeight, minWidth, spacing }: Params) => {
	const available = maxWidth - 2 * spacing;
	const topHeight = Math.round(Math.min(maxWidth / ratios[0], (maxHeight - spacing) * 0.66));
	const lineHeight = Math.round(available / (ratios[1] + ratios[2] + ratios[3]));
	const firstWidth = Math.max(minWidth, Math.round(Math.min(available * 0.4, lineHeight * ratios[1])));
	const thirdWidth = Math.round(Math.max(minWidth, available * 0.33, lineHeight * ratios[3]));
	const secondWidth = maxWidth - firstWidth - thirdWidth - 2 * spacing;
	const bottomHeight = Math.min(maxHeight - topHeight - spacing, lineHeight);
	const bottomY = topHeight + spacing;
	return [
		rect(0, 0, maxWidth, topHeight),
		rect(0, bottomY, firstWidth, bottomHeight),
		rect(firstWidth + spacing, bottomY, secondWidth, bottomHeight),
		rect(firstWidth + secondWidth + 2 * spacing, bottomY, thirdWidth, bottomHeight),
	];
};

const layoutFourLeftAndOther = ({ ratios, maxWidth, maxHeight, minWidth, spacing }: Params) => {
	const leftWidth = Math.round(Math.min(maxHeight * ratios[0], (maxWidth - spacing) * 0.6));
	const columnWidth = Math.round((maxHeight - 2 * spacing) / (1 / ratios[1] + 1 / ratios[2] + 1 / ratios[3]));
	const firstHeight = Math.round(columnWidth / ratios[1]);
	const secondHeight = Math.round(columnWidth / ratios[2]);
	const thirdHeight = maxHeight - firstHeight - secondHeight - 2 * spacing;
	const rightWidth = Math.max(minWidth, Math.min(maxWidth - leftWidth - spacing, columnWidth));
	const rightX = leftWidth + spacing;
	return [
		rect(0, 0, leftWidth, maxHeight),
		rect(rightX, 0, rightWidth, firstHeight),
		rect(rightX, firstHeight + spacing, rightWidth, secondHeight),
		rect(rightX, firstHeight + secondHeight + 2 * spacing, rightWidth, thirdHeight),
	];
};

const layoutFour = (params: Params) =>
	params.proportions[0] === "w" ? layoutFourTopAndOther(params) : layoutFourLeftAndOther(params);

const cropRatios = (ratios: number[], averageRatio: number) =>
	ratios.map((ratio) =>
		averageRatio > 1.1 ? Math.min(Math.max(ratio, 1), 2.75) : Math.min(Math.max(ratio, 0.6667), 1),
	);

const evenLines = (count: number) =>
	Array.from({ length: Math.ceil(count / 3) }, (_, line) => Math.min(3, count - line * 3));

const lineSplits = (count: number, averageRatio: number): number[][] => {
	const splits: number[][] = [];
	for (let first = 1; first < count; first++) {
		const second = count - first;
		if (first <= 3 && second <= 3) splits.push([first, second]);
	}
	for (let first = 1; first < count - 1; first++) {
		for (let second = 1; second < count - first; second++) {
			const third = count - first - second;
			if (first <= 3 && second <= (averageRatio < 0.85 ? 4 : 3) && third <= 3)
				splits.push([first, second, third]);
		}
	}
	for (let first = 1; first < count - 2; first++) {
		for (let second = 1; second < count - first - 1; second++) {
			for (let third = 1; third < count - first - second; third++) {
				const fourth = count - first - second - third;
				if (first <= 3 && second <= 3 && third <= 3 && fourth <= 3) splits.push([first, second, third, fourth]);
			}
		}
	}
	return splits.length ? splits : [evenLines(count)];
};

const layoutComplex = ({ ratios: sourceRatios, averageRatio, maxWidth, minWidth, spacing }: Params) => {
	const ratios = cropRatios(sourceRatios, averageRatio);
	const maxHeight = (maxWidth * 4) / 3;

	const attempts = lineSplits(ratios.length, averageRatio).map((counts) => {
		let offset = 0;
		const heights = counts.map((count) => {
			const lineRatios = ratios.slice(offset, offset + count);
			offset += count;
			return (maxWidth - (count - 1) * spacing) / sum(lineRatios);
		});
		return { counts, heights };
	});

	const score = ({ counts, heights }: { counts: number[]; heights: number[] }) => {
		const totalHeight = sum(heights) + spacing * (counts.length - 1);
		const tooNarrow = Math.min(...heights) < minWidth ? 1.5 : 1;
		const shrinking = counts.some((count, line) => line > 0 && counts[line - 1] > count) ? 1.5 : 1;
		return Math.abs(totalHeight - maxHeight) * tooNarrow * shrinking;
	};

	const optimal = attempts.reduce((best, attempt) => (score(attempt) < score(best) ? attempt : best));

	const items: MosaicRect[] = [];
	let y = 0;
	optimal.counts.forEach((count, line) => {
		const lineHeight = optimal.heights[line];
		const height = Math.round(lineHeight);
		let x = 0;
		for (let column = 0; column < count; column++) {
			const ratio = ratios[items.length];
			const width = column === count - 1 ? maxWidth - x : Math.round(ratio * lineHeight);
			items.push(rect(x, y, width, height));
			x += width + spacing;
		}
		y += height + spacing;
	});
	return items;
};

const layoutItems = (params: Params) => {
	const count = params.ratios.length;
	if (count === 1) return layoutOne(params);
	if (count >= 5 || params.ratios.some((ratio) => ratio > 2)) return layoutComplex(params);
	if (count === 2) return layoutTwo(params);
	if (count === 3) return layoutThree(params);
	return layoutFour(params);
};

const layoutMosaic = (
	sourceRatios: number[],
	{ width, spacing, minWidth = width / 4 }: MosaicOptions,
): MosaicLayout => {
	if (!sourceRatios.length) return { width: 0, height: 0, items: [] };

	const ratios = sourceRatios.map(toRatio);
	const items = layoutItems({
		ratios,
		proportions: ratios.map(toProportion).join(""),
		maxWidth: width,
		maxHeight: width,
		minWidth,
		spacing,
		averageRatio: sum(ratios) / ratios.length,
	});

	return {
		width: Math.max(...items.map((item) => item.x + item.width)),
		height: Math.max(...items.map((item) => item.y + item.height)),
		items,
	};
};

export default layoutMosaic;
